import { ArrayElementType } from 'common/utility-types';

export const availableAttributeTypes = [
  'bool',
  'complex',
  'datetime',
  'evaluationSeries',
  'experimentState',
  'file',
  'fileSeries',
  'fileSet',
  'float',
  'floatSeries',
  'histogramSeries',
  'gitRef',
  'histogramSeries',
  'imageSeries',
  'int',
  'string',
  'stringSeries',
  'stringSet',
  'notSupported',
] as const;

export type AttributeType = ArrayElementType<typeof availableAttributeTypes>;

export type BaseAttribute = {
  attributeName: string;
  attributeType: AttributeType;
};

export type BooleanAttribute = BaseAttribute & {
  attributeType: 'bool';
  value: boolean;
};

export type DateTimeAttribute = BaseAttribute & {
  attributeType: 'datetime';
  value: Date;
};

export type EvaluationSeriesAttribute = BaseAttribute & {
  attributeType: 'evaluationSeries';
  evaluationSeriesId: string;
};

export type ExperimentStateAttribute = BaseAttribute & {
  attributeType: 'experimentState';
  value: ExperimentState;
};

export type FileAttribute = BaseAttribute & {
  attributeType: 'file';
  mimeType: string;
  path: string;
  size: number;
};

type FileRef = {
  mimeType?: string;
  path?: string;
  sizeBytes?: number;
};

export type FileSeriesAttribute = BaseAttribute & {
  attributeType: 'fileSeries';
  lastStep?: number;
  last?: FileRef;
};

export type FileSetAttribute = BaseAttribute & {
  attributeType: 'fileSet';
  size: number;
};

export type FloatAttribute = BaseAttribute & {
  attributeType: 'float';
  value: number;
};

export type FloatSeriesAttribute = BaseAttribute & {
  attributeType: 'floatSeries';
  average?: number;
  config: FloatSeriesAttributeConfig;
  last?: number;
  lastStep?: number;
  max?: number;
  min?: number;
  variance?: number;
  hasPreview?: boolean;
};

export type GitRefAttribute = BaseAttribute & {
  attributeType: 'gitRef';
  commit: GitCommit;
  currentBranch?: string;
  remotes?: string[];
  repositoryDirty: boolean;
};

export type HistogramSeriesAttribute = BaseAttribute & {
  attributeType: 'histogramSeries';
};

export type ImageSeriesAttribute = BaseAttribute & {
  attributeType: 'imageSeries';
  channelId: string;
  lastStep?: number;
};

export type IntegerAttribute = BaseAttribute & {
  attributeType: 'int';
  value: number;
};

export type ComplexAttribute = BaseAttribute & {
  attributeType: 'complex';
  __type: {
    name: string;
  };
  __content: string;
};

export type StringAttribute = BaseAttribute & {
  attributeType: 'string';
  value: string;
};

export type StringSeriesAttribute = BaseAttribute & {
  attributeType: 'stringSeries';
  last?: string;
  lastStep?: number;
};

export type StringSetAttribute = BaseAttribute & {
  attributeType: 'stringSet';
  values: string[];
};

export type NotSupportedAttribute = BaseAttribute & {
  attributeType: 'notSupported';
  rawSource: unknown; // for convenience
};

export type Attribute =
  | BooleanAttribute
  | DateTimeAttribute
  | EvaluationSeriesAttribute
  | ExperimentStateAttribute
  | FileAttribute
  | FileSeriesAttribute
  | FileSetAttribute
  | FloatAttribute
  | FloatSeriesAttribute
  | GitRefAttribute
  | HistogramSeriesAttribute
  | ImageSeriesAttribute
  | IntegerAttribute
  | ComplexAttribute
  | StringAttribute
  | StringSeriesAttribute
  | StringSetAttribute
  | NotSupportedAttribute;

export type ExperimentState = 'running' | 'idle';

export type FloatSeriesAttributeConfig = {
  max?: number;
  min?: number;
  unit?: string;
};

export type GitCommit = {
  authorEmail: string;
  authorName: string;
  commitDate: Date;
  commitId: string;
  message: string;
};

type AttributeByTypeWithoutEvaluation<T> = T extends 'bool'
  ? BooleanAttribute
  : T extends 'datetime'
    ? DateTimeAttribute
    : T extends 'experimentState'
      ? ExperimentStateAttribute
      : T extends 'file'
        ? FileAttribute
        : T extends 'fileSet'
          ? FileSetAttribute
          : T extends 'fileSeries'
            ? FileSeriesAttribute
            : T extends 'float'
              ? FloatAttribute
              : T extends 'floatSeries'
                ? FloatSeriesAttribute
                : T extends 'gitRef'
                  ? GitRefAttribute
                  : T extends 'histogramSeries'
                    ? HistogramSeriesAttribute
                    : T extends 'imageSeries'
                      ? ImageSeriesAttribute
                      : T extends 'int'
                        ? IntegerAttribute
                        : T extends 'complex'
                          ? ComplexAttribute
                          : T extends 'string'
                            ? StringAttribute
                            : T extends 'stringSeries'
                              ? StringSeriesAttribute
                              : T extends 'stringSet'
                                ? StringSetAttribute
                                : T extends 'notSupported'
                                  ? NotSupportedAttribute
                                  : never;

export type AttributeByType<T> = T extends 'evaluationSeries'
  ? EvaluationSeriesAttribute
  : AttributeByTypeWithoutEvaluation<T>;
